import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { projectCategories } from "../../mocks/projects";
import { useScrollReveal, useDrawLine } from "../../hooks/useScrollReveal";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import GalleryImage from "./components/GalleryImage";

export default function ProjectDetailPage() {
  const { category, projectId } = useParams<{
    category: string;
    projectId: string;
  }>();
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const galleryScrollRef = useRef<HTMLDivElement | null>(null);

  const cat = projectCategories.find((c) => c.slug === category);
  const project = cat?.projects.find((p) => p.id === projectId);

  const overviewLabel = useScrollReveal<HTMLDivElement>({ delay: 80, direction: "left", distance: 20, duration: 700 });
  const overviewLine  = useDrawLine<HTMLDivElement>({ delay: 200, duration: 900 });
  const overviewText  = useScrollReveal<HTMLDivElement>({ delay: 180, direction: "up", distance: 28, duration: 750 });
  const sidebarCard   = useScrollReveal<HTMLDivElement>({ delay: 260, direction: "up", distance: 32, duration: 800 });

  const galleryLabel = useScrollReveal<HTMLDivElement>({ delay: 80, direction: "left", distance: 20, duration: 700 });
  const galleryLine  = useDrawLine<HTMLDivElement>({ delay: 200, duration: 900 });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [category, projectId]);

  if (!cat || !project) {
    return (
      <div className="min-h-screen bg-off-white flex flex-col items-center justify-center px-6">
        <h1 className="font-serif text-4xl text-charcoal mb-4">
          Project not found
        </h1>
        <Link
          to="/"
          className="font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const handleNavigate = (id: string) => {
    if (id === "home") {
      navigate("/");
    } else {
      navigate(`/#${id}`);
    }
  };

  const allImages = Array.from(new Set([project.thumbnail, ...project.images]));
  const hasCaseStudy = project.challenge || project.designResponse || project.responsibilities?.length;

  return (
    <div className="min-h-screen bg-off-white">
      <Navbar onNavigate={handleNavigate} />

      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 font-sans text-sm text-warm-gray hover:text-teal transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back
            </button>
            <span className="text-teal/30">/</span>
            <span className="font-sans text-sm text-charcoal">
              {project.title}
            </span>
          </div>

          <div className="relative h-[320px] md:h-[560px] rounded-xl overflow-hidden mb-10 md:mb-14 shadow-sm">
            <img
              src={project.thumbnail}
              alt={project.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/45" />
            <div className="absolute inset-0 bg-gradient-to-tr from-teal/10 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-4xl">
              <h1 className="font-serif text-3xl md:text-6xl text-white mb-3">
                {project.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-5 h-px bg-teal/70" />
                <p className="font-sans text-sm text-white/85">
                  {project.location} &middot; {project.year}
                </p>
                {project.year.toLowerCase().includes("under construction") && (
                  <span className="font-sans text-xs uppercase tracking-[1.5px] bg-terracotta/90 text-white px-3 py-1 rounded-full whitespace-nowrap">
                    Under Construction
                  </span>
                )}
              </div>
            </div>
          </div>
          {project.pullQuotes && project.pullQuotes.length > 0 && (
            <div className="mb-10 md:mb-14 bg-warm-beige/70 rounded-xl px-6 py-7 md:px-10 md:py-9 border-l-4 border-teal/45">
              <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta mb-4">Takeaway</p>
              <div className="space-y-4">
                {project.pullQuotes.map((quote) => (
                  <p key={quote} className="font-serif text-2xl md:text-3xl leading-snug text-charcoal">
                    “{quote}”
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16 mb-12 md:mb-16">
            <div className="lg:col-span-2">
              <div ref={overviewLabel} className="flex items-center gap-4 mb-6">
                <h2 className="font-serif text-2xl md:text-3xl text-charcoal whitespace-nowrap">
                  Project Overview
                </h2>
                <div className="flex-1 overflow-hidden">
                  <div
                    ref={overviewLine}
                    style={{ width: "0%" }}
                    className="h-[2px] bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
                  />
                </div>
              </div>
              <div ref={overviewText}>
                <p className="font-sans text-base text-charcoal/80 leading-[1.8] border-l-2 border-teal/25 pl-5">
                  {project.description}
                </p>
              </div>

              <div className="mt-10 md:mt-12">
                <div className="flex items-center gap-4 mb-6 md:mb-8 overflow-hidden">
                  <div ref={galleryLabel}>
                    <h2 className="font-serif text-2xl md:text-3xl text-charcoal whitespace-nowrap">
                      Gallery
                    </h2>
                  </div>
                  <div className="flex-1">
                    <div
                      ref={galleryLine}
                      style={{ width: "0%" }}
                      className="h-[2px] bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
                    />
                  </div>
                  <span className="font-sans text-xs text-warm-gray tracking-wider hidden sm:block whitespace-nowrap">
                    {allImages.length} images
                  </span>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    aria-label="Scroll gallery left"
                    onClick={() => galleryScrollRef.current?.scrollBy({ left: -420, behavior: "smooth" })}
                    className="hidden md:flex absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/90 border border-teal/10 shadow-sm text-charcoal hover:text-teal transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-2xl" />
                  </button>
                  <div
                    ref={galleryScrollRef}
                    className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 pr-10 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  >
                    {allImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="snap-start flex-none w-[78%] sm:w-[46%] xl:w-[31%]"
                      >
                        <GalleryImage
                          src={img}
                          alt={`${project.title} - Image ${idx + 1}`}
                          index={idx}
                          total={allImages.length}
                          onClick={() => setLightboxIndex(idx)}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    aria-label="Scroll gallery right"
                    onClick={() => galleryScrollRef.current?.scrollBy({ left: 420, behavior: "smooth" })}
                    className="hidden md:flex absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/90 border border-teal/10 shadow-sm text-charcoal hover:text-teal transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-2xl" />
                  </button>
                </div>
                {allImages.length > 1 && (
                  <div className="mt-4 flex justify-center gap-2">
                    {allImages.map((_, idx) => (
                      <span
                        key={idx}
                        className={`h-2 w-2 rounded-full ${idx === 0 ? "bg-teal/70" : "bg-teal/20"}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div ref={sidebarCard} className="bg-white rounded-xl p-6 md:p-8 border border-teal/10 shadow-sm">
                <h3 className="font-sans text-xs uppercase tracking-[2px] text-teal mb-6">
                  Project Details
                </h3>
                <div className="space-y-4">
                  {(project.details ?? [
                    { label: "Location", value: project.location },
                    { label: "Year", value: project.year },
                  ]).map((detail) => (
                    <div key={`${detail.label}-${detail.value}`}>
                      <p className="font-sans text-xs text-warm-gray mb-1">
                        {detail.label}
                      </p>
                      {detail.url ? (
                        <a
                          href={detail.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
                        >
                          {detail.value}
                          <i className="ri-external-link-line text-xs" />
                        </a>
                      ) : (
                        <p className="font-sans text-sm text-charcoal">
                          {detail.value}
                        </p>
                      )}
                      <div className="h-px bg-teal/10 mt-4" />
                    </div>
                  ))}

                  {project.pdfUrl && project.pdfUrl !== "#" && (
                    <div>
                      <p className="font-sans text-xs text-warm-gray mb-1">
                        Documents
                      </p>
                      <a
                        href={project.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
                      >
                        <i className="ri-download-line" />
                        Download Current Set
                      </a>
                    </div>
                  )}

                  {project.ffePdfUrl && (
                    <div>
                      <p className="font-sans text-xs text-warm-gray mb-1">
                        FF&E Presentation
                      </p>
                      <a
                        href={project.ffePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
                      >
                        <i className="ri-file-pdf-line" />
                        View FF&E Presentation
                      </a>
                    </div>
                  )}

                  {project.websiteUrl && (
                    <div>
                      <p className="font-sans text-xs text-warm-gray mb-1">
                        Property Website
                      </p>
                      <a
                        href={project.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line" />
                        Visit Website
                      </a>
                    </div>
                  )}

                  {project.presentationUrl && (
                    <div>
                      <p className="font-sans text-xs text-warm-gray mb-1">
                        Design Presentation
                      </p>
                      <a
                        href={project.presentationUrl}
                        download
                        className="inline-flex items-center gap-2 font-sans text-sm text-terracotta hover:text-charcoal transition-colors cursor-pointer"
                      >
                        <i className="ri-file-pdf-line" />
                        Download Presentation
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {hasCaseStudy && (
            <div className="mt-14 md:mt-20 space-y-5 md:space-y-6">
              {project.challenge && (
                <article className="bg-white rounded-xl p-6 md:p-8 border border-teal/10 shadow-sm">
                  <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta mb-4">Challenge</p>
                  <p className="font-sans text-sm md:text-base text-charcoal/75 leading-relaxed">{project.challenge}</p>
                </article>
              )}
              {project.designResponse && (
                <article className="bg-white rounded-xl p-6 md:p-8 border border-teal/10 shadow-sm">
                  <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta mb-4">Design Response</p>
                  <p className="font-sans text-sm md:text-base text-charcoal/75 leading-relaxed">{project.designResponse}</p>
                </article>
              )}
              {project.responsibilities && project.responsibilities.length > 0 && (
                <article className="bg-white rounded-xl p-6 md:p-8 border border-teal/10 shadow-sm">
                  <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta mb-4">Responsibilities</p>
                  <ul className="space-y-3">
                    {project.responsibilities.map((item) => (
                      <li key={item} className="font-sans text-sm md:text-base text-charcoal/75 leading-relaxed flex gap-2">
                        <span className="text-teal mt-[1px]">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              )}
            </div>
          )}

        </div>
      </section>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
            onClick={() => setLightboxIndex(null)}
          >
            <i className="ri-close-line text-2xl" />
          </button>

          {lightboxIndex > 0 && (
            <button
              className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(lightboxIndex - 1);
              }}
            >
              <i className="ri-arrow-left-line text-2xl" />
            </button>
          )}

          {lightboxIndex < allImages.length - 1 && (
            <button
              className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(lightboxIndex + 1);
              }}
            >
              <i className="ri-arrow-right-line text-2xl" />
            </button>
          )}

          <div className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={allImages[lightboxIndex]}
              alt={`${project.title} - Full view`}
              className="max-w-full max-h-[85vh] object-contain rounded-md"
            />
          </div>

          <p className="absolute bottom-6 font-sans text-sm text-teal/70">
            {lightboxIndex + 1} / {allImages.length}
          </p>
        </div>
      )}

      <Footer />
    </div>
  );
}
